import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import ReportPhoto from "../components/ReportPhoto";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { formatUSD, formatDate, formatDateTime, daysBetween } from "../lib/format";
import { toast } from "sonner";
import { CaretLeft, CheckCircle, Circle, Wrench, Package, Users, Clock, Sparkle, Prohibit, X } from "@phosphor-icons/react";
import QuillaIA from "../components/QuillaIA";

// Línea de tiempo canónica del ciclo — se muestran solo las etapas que aplican a la ruta.
const TIMELINE_ORDER = [
  { key: "REPORTADO",         label: "Enviado" },
  { key: "VALIDADO",          label: "Validado por mantenimiento" },
  { key: "APROBADO",          label: "Aprobado por gerencia" },
  { key: "ESPERANDO_COMPRA",  label: "En compras" },
  { key: "ESPERANDO_REPUESTO",label: "Esperando repuesto" },
  { key: "EN_MANTENIMIENTO",  label: "En ejecución" },
  { key: "CONTRATISTA",       label: "En manos del contratista" },
  { key: "CERRADO",           label: "Resuelto" },
  { key: "RECHAZADO",         label: "Cerrado (rechazado)" },
];

// Índice del estado actual para colorear el timeline
const STATUS_INDEX = {
  REPORTADO: 0,
  VALIDADO: 1,
  ESPERANDO_COMPRA: 3,
  ESPERANDO_REPUESTO: 4,
  EN_MANTENIMIENTO: 5,
  CONTRATISTA: 6,
  CERRADO: 7,
  RECHAZADO: 8,
};

function pickTimelineFor(rep) {
  const cur = STATUS_INDEX[rep.status] ?? 0;
  const items = [];
  items.push({ key: "REPORTADO", label: "Enviado", done: cur >= 0 });
  items.push({ key: "VALIDADO", label: "Validado por mantenimiento", done: cur >= 1 });
  if (rep.route === "repuesto" || rep.status === "ESPERANDO_COMPRA" || rep.status === "ESPERANDO_REPUESTO") {
    items.push({ key: "ESPERANDO_COMPRA", label: "Aprobado, en compras", done: cur >= 3 });
    const dias = rep.purchase_confirmed_at ? daysBetween(rep.purchase_confirmed_at) : 0;
    items.push({ key: "ESPERANDO_REPUESTO", label: `Esperando repuesto${rep.status === "ESPERANDO_REPUESTO" ? ` · ${dias} día${dias === 1 ? "" : "s"}` : ""}`, done: cur >= 4 });
    items.push({ key: "EN_MANTENIMIENTO", label: "En ejecución", done: cur >= 5 });
  } else if (rep.route === "contratista") {
    items.push({ key: "CONTRATISTA", label: "En manos del contratista", done: cur >= 6 });
  } else {
    items.push({ key: "EN_MANTENIMIENTO", label: "En ejecución", done: cur >= 5 });
  }
  if (rep.status === "RECHAZADO") {
    return [items[0], { key: "RECHAZADO", label: "Cerrado (rechazado)", done: true, current: true }];
  }
  items.push({ key: "CERRADO", label: "Resuelto", done: rep.status === "CERRADO" });
  // marcamos el actual
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].done) { items[i].current = true; break; }
  }
  return items;
}

export default function ReportDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { session } = useAuth();
  const [rep, setRep] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [memory, setMemory] = useState(null);
  const [showCopilot, setShowCopilot] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const role = session?.user?.role;

  useEffect(() => {
    api.get(`/reports/${id}`).then(r => setRep(r.data)).catch(() => toast.error("No se pudo cargar el reporte"));
    api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => {});
    // deps reales: solo `id`. api/toast/setters son estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (rep && (role === "mantenimiento" || role === "gerencia")) {
      api.get(`/ai/asset-memory/${id}`).then(r => setMemory(r.data)).catch(() => {});
    }
    // deps reales cubiertos: rep, id, role.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rep, id, role]);

  if (!rep) return <AppShell title="Cargando…"><div className="q-card p-6">Cargando reporte…</div></AppShell>;

  const supplier = suppliers.find(s => s.id === rep.supplier_id);
  const tl = pickTimelineFor(rep);

  const submitCancel = async () => {
    const reason = cancelReason.trim();
    if (reason.length < 3) { toast.error("El motivo es obligatorio"); return; }
    setCancelling(true);
    try {
      await api.post(`/reports/${rep.id}/cancel`, { reason });
      toast.success("Reporte anulado. Excluido del Health Index, KPIs y colas.");
      setShowCancel(false);
      // recargar el reporte para reflejar el estado ANULADO
      const { data } = await api.get(`/reports/${rep.id}`);
      setRep(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo anular");
    } finally { setCancelling(false); }
  };

  const canCancel = role === "gerencia" && !["CERRADO", "RECHAZADO", "ANULADO"].includes(rep.status);
  const isCancelled = rep.status === "ANULADO";

  const backTo = {
    housekeeping: "/housekeeping",
    mantenimiento: "/mantenimiento",
    compras: "/compras",
    gerencia: "/gerencia",
  }[role] || "/";

  return (
    <AppShell>
      <button data-testid="detail-back" onClick={() => nav(backTo)} className="q-btn-ghost mb-4 inline-flex items-center gap-2 text-sm min-h-[44px]">
        <CaretLeft size={14}/> Volver
      </button>

      {isCancelled && (
        <div className="q-card p-4 mb-4 border-l-4 border-rose-500 bg-rose-50" data-testid="cancelled-banner">
          <div className="flex items-center gap-2 text-rose-700 font-semibold mb-1">
            <Prohibit size={18} weight="fill"/> Reporte ANULADO
          </div>
          <div className="text-sm text-rose-800">
            Anulado por <span className="font-semibold">{rep.cancelled_by}</span> · {formatDateTime(rep.cancelled_at)}
          </div>
          <div className="text-sm text-rose-900 mt-1"><span className="q-label text-rose-600">Motivo:</span> {rep.cancel_reason}</div>
        </div>
      )}

      {/* Cabecera */}
      <div className="q-card p-5 mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div>
            <div className="q-label text-[color:var(--q-muted)]">Habitación</div>
            <div className="text-5xl font-mono font-light text-[color:var(--q-navy)]">{rep.room}</div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`chip chip-${rep.severity}`}>{rep.severity?.toUpperCase()}</span>
              {rep.route && (
                <span className="badge-route" style={{background: rep.route === "interna" ? "#1F3864" : rep.route === "repuesto" ? "#C9A227" : "#4b5563", color: rep.route === "repuesto" ? "#1a1200" : "#fff"}}>
                  {rep.route}
                </span>
              )}
              {rep.is_security && <span className="chip chip-grave text-[10px]">SEGURIDAD</span>}
            </div>
            <div className="text-lg font-semibold">{rep.note || "Sin nota"}</div>
            <div className="text-xs text-[color:var(--q-muted)] mt-1">
              Reportado por {rep.reporter_name} · {formatDateTime(rep.created_at)}
            </div>
          </div>
        </div>

        {rep.photo_paths?.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="detail-photos">
            {rep.photo_paths.map(p => <ReportPhoto key={p} path={p} className="w-full h-40"/>)}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="q-card p-5 mb-4" data-testid="detail-timeline">
        <div className="q-label text-[color:var(--q-muted)] mb-3">Estado</div>
        <ol className="relative">
          {tl.map((it, i) => (
            <li key={it.key} className={`flex items-start gap-3 pb-4 ${i === tl.length - 1 ? "" : "border-l-2 ml-3"}`} style={{borderColor: it.done ? "#1F3864" : "#e6e2d8"}}>
              <div className="-ml-4 mt-0.5">
                {it.done ? (
                  <CheckCircle size={20} weight="fill" className={it.current ? "text-[color:var(--q-gold)]" : "text-[color:var(--q-navy)]"}/>
                ) : (
                  <Circle size={20} className="text-neutral-300"/>
                )}
              </div>
              <div>
                <div className={`text-sm ${it.current ? "font-bold text-[color:var(--q-navy)]" : it.done ? "font-semibold" : "text-[color:var(--q-muted)]"}`}>
                  {it.label}{it.current ? " · ahora" : ""}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Vistas por rol */}
      {(role === "mantenimiento" || role === "gerencia") && (
        <div className="q-card p-5 mb-4" data-testid="detail-tech">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Ficha técnica</h3>
            <button onClick={() => setShowCopilot(true)} data-testid="detail-copilot" className="q-btn-ghost text-xs flex items-center gap-1 border-[color:var(--q-navy)] text-[color:var(--q-navy)]">
              <Sparkle size={12} weight="fill"/> QUILLA IA
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="q-label text-[color:var(--q-muted)]">Activo</dt><dd>{rep.asset_hint || "—"}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Ruta</dt><dd>{rep.route || "sin clasificar"}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Nota técnica</dt><dd>{rep.tech_note || "—"}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Fuera de servicio</dt><dd>{rep.out_of_service ? "Sí" : "No"}</dd></div>
          </dl>
          {memory && (memory.history?.length > 1 || memory.summary) && (
            <div className="mt-4 p-3 rounded-lg border border-[color:var(--q-gold)]/40 bg-[color:var(--q-gold)]/5">
              <div className="q-label text-[color:var(--q-gold-dark)] mb-1 flex items-center gap-2">
                <Sparkle size={12} weight="fill"/> Memoria del activo
                {memory.reincidence && <span className="chip chip-grave text-[10px]">REINCIDENCIA</span>}
              </div>
              <div className="text-xs text-[color:var(--q-muted)] mb-1">
                {memory.recent_90d} reportes en 90 días · gastado histórico {formatUSD(memory.total_spent_usd)}
              </div>
              {memory.summary && <div className="text-sm leading-relaxed whitespace-pre-wrap">{memory.summary}</div>}
            </div>
          )}
        </div>
      )}

      {(role === "compras" || role === "gerencia") && (rep.status === "ESPERANDO_COMPRA" || rep.status === "ESPERANDO_REPUESTO" || rep.route === "repuesto") && (
        <div className="q-card p-5 mb-4" data-testid="detail-purchase">
          <h3 className="font-semibold mb-3">Requisición</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="q-label text-[color:var(--q-muted)]">Proveedor</dt><dd className="font-semibold">{supplier?.name || "Sin asignar"}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Categoría</dt><dd>{supplier?.category || "—"}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Precio</dt><dd className="font-mono font-semibold">{formatUSD(rep.estimated_cost || 0)}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">ETA repuesto</dt><dd className="font-mono">{rep.eta ? formatDate(rep.eta) : "—"}</dd></div>
            {rep.purchase_confirmed_at && (
              <div className="col-span-2"><dt className="q-label text-[color:var(--q-muted)]">Compra confirmada</dt>
                <dd>{formatDateTime(rep.purchase_confirmed_at)} · {daysBetween(rep.purchase_confirmed_at)} día(s) esperando</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {role === "gerencia" && (
        <div className="q-card p-5 mb-4" data-testid="detail-cost">
          <h3 className="font-semibold mb-3">Costos y auditoría</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="q-label text-[color:var(--q-muted)]">Costo estimado</dt><dd className="font-mono font-semibold">{formatUSD(rep.estimated_cost || 0)}</dd></div>
            <div><dt className="q-label text-[color:var(--q-muted)]">Aprobado por</dt><dd>{rep.approved_by || "—"}</dd></div>
          </dl>
          <div className="q-label text-[color:var(--q-muted)] mt-4 mb-2">Bitácora completa</div>
          <ol className="text-xs space-y-1">
            {(rep.history || []).map((h, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono text-[color:var(--q-muted)]">{formatDateTime(h.ts)}</span>
                <span className="font-semibold">{h.by}</span>
                <span>{h.action}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {showCopilot && <QuillaIA context="copilot" reportId={rep.id} onClose={() => setShowCopilot(false)}/>}

      {/* Botón Anular · sólo gerencia y reportes vivos */}
      {canCancel && (
        <div className="q-card p-4 border-l-4 border-rose-300">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-sm">Anular este reporte</div>
              <div className="text-xs text-[color:var(--q-muted)] mt-0.5">
                Se conserva en la bitácora, pero deja de contar en el Health Index, KPIs y colas del equipo.
              </div>
            </div>
            <button onClick={() => setShowCancel(true)} data-testid="cancel-report-btn"
                    className="q-btn-ghost text-rose-700 border-rose-400 hover:bg-rose-50 flex items-center gap-1.5 shrink-0">
              <Prohibit size={14} weight="fill"/> Anular
            </button>
          </div>
        </div>
      )}

      {showCancel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
             onClick={() => setShowCancel(false)} data-testid="cancel-modal">
          <div className="q-card w-full max-w-md p-5 rounded-t-2xl sm:rounded-2xl q-safe-bottom" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Prohibit size={18} weight="fill" className="text-rose-600"/> Anular reporte
                </h3>
                <p className="text-xs text-[color:var(--q-muted)] mt-1">
                  Hab. {rep.room} · {rep.note?.slice(0,60)}
                </p>
              </div>
              <button onClick={() => setShowCancel(false)} className="text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)]">
                <X size={18}/>
              </button>
            </div>
            <label className="q-label block mb-1 text-[color:var(--q-muted)]">Motivo (obligatorio)</label>
            <textarea data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                      autoFocus placeholder="Ej: duplicado, reporte creado por error, resuelto por invitado…"
                      className="w-full h-24 p-2 rounded-lg border border-[color:var(--q-border)] text-sm"/>
            <div className="flex gap-2 justify-end mt-4">
              <button className="q-btn-ghost" onClick={() => setShowCancel(false)}>Cancelar</button>
              <button data-testid="cancel-submit-btn" onClick={submitCancel} disabled={cancelling || cancelReason.trim().length < 3}
                      className="q-btn-primary bg-rose-600 hover:bg-rose-700 flex items-center gap-1.5">
                <Prohibit size={14} weight="fill"/> {cancelling ? "Anulando…" : "Anular reporte"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
